import { FC, Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { RefreshCw, Pause, Play, Rocket, RotateCw, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ContextStore from '../../hooks/ContextStore';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

// ─── Listing strategies ─────────────────────────────────────────────────────────
// Every named way we create listings (Store Spread, Category Spread, Winner Clone…), what it does, where it
// stands and the buttons to start / pause / run it. Backend: controllers/listing-strategies.js

interface Counts { total?: number; planned?: number; sent?: number; failed?: number; skipped?: number; waiting?: number; tonight?: { sent: number; failed: number }; packs?: number; jumia?: any; marjane?: any; [k: string]: any }
interface Strategy {
  key: string; emoji: string; name: string; platform: string; what: string; pace: string;
  impact?: { sales: string; profit: string; risk: string; note: string; when: string; how: string } | null;
  status: string; tone: 'green' | 'amber' | 'blue' | 'grey'; detail: string; actions: string[]; link?: string;
  counts: Counts | null; settings?: Record<string, any>;
  creators?: { name: string; packs: number }[]; recent?: { name: string; by: string; at: string }[];
  results?: { orders: number; delivered: number; last7: number; listings: number; selling: number };
  lastPlan?: { at: string; since: string; by: string; winners: { fiche: string; group: string; name: string; sku: string; store: string; jumia: number; marjane: number; copies?: { jumia: number; marjane: number } | null }[]; repeats?: { fiche: string; name: string; sku: string; store: string }[]; jumiaPromoted?: number; marjanePromoted?: number } | null;
}
interface Overview { strategies: Strategy[]; queues: { jumia: any; marjane: any }; now: string }

const TONE: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-800', amber: 'bg-amber-100 text-amber-800', blue: 'bg-sky-100 text-sky-800', grey: 'bg-slate-100 text-slate-600',
};
const ACTION: Record<string, { label: string; icon: FC<any>; confirm?: (s: Strategy) => string; primary?: boolean }> = {
  pause:  { label: 'Pause', icon: Pause },
  resume: { label: 'Resume', icon: Play, primary: true },
  start:  { label: 'Start', icon: Rocket, primary: true, confirm: (s) => `Start ${s.name}? New copies are planned and go out at the night pace.` },
  run:    { label: 'Run now', icon: Rocket, primary: true, confirm: () => 'Find today\'s winners now and put their copies first in line? (It also runs by itself every day at 19:45.)' },
  replan: { label: 'Re-plan', icon: RotateCw, confirm: (s) => `Look for new copies to plan for ${s.name}? (Nothing already sent is repeated.)` },
  open:   { label: 'Open', icon: ExternalLink },
};
const n = (x?: number) => (x || 0).toLocaleString('fr-MA');
const when = (d?: string) => (d ? new Date(d).toLocaleString('fr-MA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export const StrategiesPanel: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token: string = contextStore?.token;
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await dispatch(actions.listingStrategies, {}, token);
    setLoading(false);
    if (r && !r.error) setData(r); else setFlash({ ok: false, text: r?.error?.message || 'Could not load the strategies' });
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const act = async (s: Strategy, action: string) => {
    if (action === 'open') { if (s.link) navigate(s.link); return; }
    const a = ACTION[action];
    if (a?.confirm && !window.confirm(a.confirm(s))) return;
    setBusy(`${s.key}:${action}`); setFlash(null);
    const r = await dispatch(actions.listingStrategyAction, { key: s.key, action }, token);
    setBusy(null);
    setFlash(r?.error ? { ok: false, text: r.error.message || 'Failed' } : { ok: true, text: `${s.emoji} ${s.name}: ${r?.message || 'done'}` });
    await load();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Every way we create listings, with a name you can call. Night window {data ? `${data.queues.jumia.hourStart}:00 → ${data.queues.jumia.hourEnd}:00` : ''} · Jumia queue {data ? (data.queues.jumia.enabled ? 'on' : 'off') : '…'} · Marjane queue {data ? (data.queues.marjane.enabled ? 'on' : 'off') : '…'}
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /> Refresh</Button>
      </div>
      {flash && <p className={cn('rounded-lg border p-2.5 text-sm', flash.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700')}>{flash.text}</p>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[1120px] text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="p-2 font-medium">Strategy</th>
              <th className="p-2 font-medium">What it does</th>
              <th className="p-2 font-medium">Status</th>
              <th className="p-2 text-right font-medium" title="Copies planned and waiting for the night">Waiting</th>
              <th className="p-2 text-right font-medium" title="Copies sent / failed since the night window opened">Tonight</th>
              <th className="p-2 text-right font-medium" title="All time: sent · failed · skipped">All time</th>
              <th className="p-2 text-right font-medium" title="Orders on the listings this strategy created (cancelled excluded) · delivered · last 7 days · how many of its listings have sold at least once">Orders</th>
              <th className="p-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={8} className="p-6 text-center text-slate-400">{loading ? 'Reading the queues…' : 'Nothing to show'}</td></tr>}
            {data?.strategies.map((s) => {
              const c = s.counts || {};
              const expandable = s.key === 'winner' || s.key === 'bundles' || !!s.settings;
              const isOpen = open === s.key;
              return (
                <Fragment key={s.key}>
                  <tr className="border-b border-slate-100 align-top hover:bg-slate-50/60">
                    <td className="p-2">
                      <button className="flex items-start gap-1.5 text-left" onClick={() => expandable && setOpen(isOpen ? null : s.key)}>
                        {expandable ? (isOpen ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 text-slate-400" />) : <span className="w-3.5" />}
                        <span>
                          <span className="block whitespace-nowrap text-sm font-bold text-slate-800">{s.emoji} {s.name}</span>
                          <span className="text-[11px] text-slate-400">{s.platform}</span>
                        </span>
                      </button>
                    </td>
                    <td className="max-w-[360px] p-2 text-slate-600">{s.what}<p className="mt-0.5 text-[11px] text-slate-400">{s.pace}</p>
                      {s.impact && (
                        <div className="mt-1.5 rounded-lg bg-slate-50 p-1.5">
                          <div className="flex flex-wrap gap-1">
                            {([['sales', 'Sales'], ['profit', 'Profit'], ['risk', 'Risk']] as const).map(([k, label]) => {
                              const v = (s.impact as any)[k] as string; const good = k === 'risk' ? v === 'low' : v === 'high'; const bad = k === 'risk' ? v === 'high' : v === 'low';
                              return <span key={k} className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', good ? 'bg-emerald-100 text-emerald-800' : bad ? 'bg-red-100 text-red-700' : v === 'unknown' ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-800')}>{label}: {v}</span>;
                            })}
                          </div>
                          <p className="mt-1 text-[11px] text-slate-600">{s.impact.note}</p>
                          <p className="mt-0.5 text-[11px]"><b>When:</b> {s.impact.when}</p>
                          <p className="text-[11px]"><b>How:</b> {s.impact.how}</p>
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      <span className={cn('inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold', TONE[s.tone])}>{s.status}</span>
                      {s.detail && <p className="mt-1 max-w-[220px] text-[11px] text-slate-500">{s.detail}</p>}
                    </td>
                    {s.key === 'bundles' ? (
                      <td colSpan={3} className="p-2 text-right text-slate-600">{n(c.packs)} packs · {n(c.jumia)} Jumia listings · {n(c.marjane)} Marjane listings</td>
                    ) : (
                      <>
                        <td className="p-2 text-right text-sm font-semibold text-slate-800">{n(c.waiting)}</td>
                        <td className="p-2 text-right whitespace-nowrap"><b className="text-emerald-700">{n(c.tonight?.sent)}</b>{c.tonight?.failed ? <span className="text-red-600"> · {n(c.tonight.failed)} ✗</span> : null}</td>
                        <td className="p-2 text-right whitespace-nowrap text-slate-600">{n(c.sent)} sent{c.failed ? <span className="text-red-600"> · {n(c.failed)} failed</span> : null}{c.skipped ? <span className="text-slate-400"> · {n(c.skipped)} skipped</span> : null}</td>
                      </>
                    )}
                    <td className="p-2 text-right whitespace-nowrap">
                      {s.results ? (
                        <>
                          <span className="text-sm font-bold text-slate-800">{n(s.results.orders)}</span>
                          <span className="text-slate-500"> · {n(s.results.delivered)} delivered</span>
                          {s.results.last7 ? <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] font-bold text-emerald-700">+{n(s.results.last7)} 7d</span> : null}
                          <p className="text-[11px] text-slate-400">{n(s.results.selling)} of {n(s.results.listings)} listings sold{s.results.listings ? ` · ${((s.results.orders / s.results.listings) * 100).toFixed(1)} per 100` : ''}</p>
                        </>
                      ) : '—'}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {s.actions.map((a) => {
                          const def = ACTION[a]; if (!def) return null;
                          const k = `${s.key}:${a}`;
                          return (
                            <Button key={a} size="sm" variant={def.primary ? 'default' : 'outline'} disabled={!!busy} onClick={() => act(s, a)}>
                              {busy === k ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <def.icon className="h-3.5 w-3.5" />} {def.label}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-slate-100 bg-slate-50/70">
                      <td colSpan={8} className="p-3 text-[11px] text-slate-600">
                        {s.settings && <p className="mb-1">Settings: {Object.entries(s.settings).map(([k, v]) => <span key={k} className="mr-3"><b>{k}</b> {String(v)}</span>)}</p>}
                        {s.key === 'bundles' && (
                          <>
                            <p className="mb-1 font-semibold">Who created them</p>
                            <div className="mb-2 flex flex-wrap gap-1">{(s.creators || []).map((c) => <span key={c.name} className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-sky-800">{c.name} · <b>{c.packs}</b></span>)}</div>
                            <p className="mb-1 font-semibold">Latest bundles</p>
                            <table className="w-full"><tbody>{(s.recent || []).map((b, i) => <tr key={i} className="border-t border-slate-200"><td className="py-0.5 pr-2">{b.name}</td><td className="py-0.5 pr-2 text-sky-800">{b.by}</td><td className="py-0.5 text-slate-400">{when(b.at)}</td></tr>)}</tbody></table>
                            <p className="mt-1 text-slate-400">The creator is recorded from 12 Oct; earlier bundles show “unknown”.</p>
                          </>
                        )}
                        {s.key === 'winner' && (
                          <>
                            <p className="mb-1"><b>{n(c.cloned)}</b> product pages cloned so far — the SKU that got the order is the winner and its own page is cloned, once. It goes, exactly as it is, into every Jumia store × every fitting category (even next to other pages of the same product). Copies made by Store/Category Spread are winners too when they sell; only copies made by Winner Clone itself never trigger again.</p>
                            <p className="mb-1">Jumia: {n(c.jumia?.planned)} waiting · {n(c.jumia?.sent)} sent · Marjane: {n(c.marjane?.planned)} waiting · {n(c.marjane?.sent)} sent</p>
                            {s.lastPlan ? (
                              <>
                                <p className="mb-1">Last plan {when(s.lastPlan.at)} ({s.lastPlan.by}) — orders since {when(s.lastPlan.since)} · {s.lastPlan.winners.length} winners · +{n(s.lastPlan.jumiaPromoted)} Jumia / +{n(s.lastPlan.marjanePromoted)} Marjane copies first in line</p>
                                <div className="flex flex-wrap gap-1">
                                  {s.lastPlan.winners.map((w) => <span key={w.fiche} title={`${w.sku} · ${w.store} · ${w.jumia} Jumia / ${w.marjane} Marjane orders`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800">{w.name} <span className="opacity-60">{w.sku}{w.copies ? ` · +${w.copies.jumia + w.copies.marjane}` : ''}</span></span>)}
                                </div>
                                {!!s.lastPlan.repeats?.length && <p className="mt-2 text-slate-500">🔁 Sold again, already cloned (not repeated): {s.lastPlan.repeats.map((w) => `${w.name} (${w.sku})`).join(', ')}</p>}
                              </>
                            ) : <p>No plan yet — first one at 19:45, or press “Run now”.</p>}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
